import React, {useState, useEffect, useCallback, useRef} from 'react';
import {View, TouchableOpacity, ActivityIndicator, Image, TextInput, ScrollView, Platform} from 'react-native';
import EStyleSheet from "react-native-extended-stylesheet";
import {CancelToken} from "axios";

import AppText from "../../../components/AppText";
import MessagesService from "../../../services/MessagesService";
import {basicStyles} from "../../../styles/basic";
import {formatDate, formatPhoneNumber, getDateDifferenceInSeconds} from "../../../utilities/methods";
import MessageFeedResolvedSvg from "../../../components/svgs/MessageFeedResolvedSvg";
import MessageFeedUnresolvedSvg from "../../../components/svgs/MessageFeedUnresolvedSvg";
import {AVATAR_MESSENGER, AVATAR_PATH_ORG, AVATAR_PATH_USER, MESSAGE_ATTACHMENT_FILE_PATH} from "../../../config";
import Avatar from "../../../components/Avatar";
import AttachmentsSvg from "../../../components/svgs/AttachmentsSvg";
import ScriptsSvg from "../../../components/svgs/ScriptsSvg";
import NotesSvg from "../../../components/svgs/NotesSvg";
import EmojisSvg from "../../../components/svgs/EmojisSvg";
import BlueCircleArrowSvg from "../../../components/svgs/BlueCircleArrowRightSvg";
import CheckMarkSvg from "../../../components/svgs/CheckMarkSvg";
import AutoExpandingTextInput from "../../../components/AutoExpandingTextInput";
import BlockModal from "./BlockModal";
import MessageFeedCustomerInfo from "./MessageFeedCustomerInfo";
import CreateNewNoteModal from "./CreateNewNoteModal";
import ActionIcon from "./ActionIcon";
import RemoveSvg from "../../../components/svgs/RemoveSvg";
import EmojiSelector from "./EmojiSelector";
import InfoSvg from "../../../components/svgs/InfoSvg";
import {navigate} from "../../../utilities/navigation";
import ChevronLeftBlueSvg from "../../../components/svgs/ChevronLeftBlueSvg";
import ScriptModal from "./ScriptModal";
import GrammarReviewModal from "./GrammarReviewModal";
import {connect} from 'react-redux';
import MessageHtmlFrame from "../../../components/MessageHtmlFrame";
import EyeSvg from "../../../components/svgs/EyeSvg";
import MessageHtmlModal from "./MessageHtmlModal";
import ExpandSvg from "../../../components/svgs/ExpandSvg";
import DarkModeToggle from "./DarkModeToggle";
import {useDarkMode} from "../DarkModeContext";
import {getThemeTransitionStyle} from "../theme";
import {useDimensions} from "../../../hooks/useDimensions";


const MESSAGES_PER_PAGE = 20;
const TYPING_POLL_INTERVAL_MS = 10000;
const DELIVERY_CONFIRMATION_TIMEOUT_MS = 25 * 1000;
const DELIVERY_CONFIRMATION_WAIT_MS = 4 * 60 * 1000;

const normalizeMessageBody = (body) => (body || '').trim();

const doesMessageConfirmPendingDelivery = (pendingMessage, persistedMessage) => {
    if (!pendingMessage?.is_pending_message
        || persistedMessage?.message_bound_type !== 'outbound'
        || !persistedMessage?.textline_post_uuid) {
        return false;
    }

    if (normalizeMessageBody(pendingMessage.message_body) !== normalizeMessageBody(persistedMessage.message_body)) {
        return false;
    }

    return true;
};

// get timestamp from feed items
const getItemTimestamp = (item) => {
    switch (item.feedItemType) {
        case 'message': return item.message_created_timestamp;
        case 'note': return item.message_feed_note_timestamp;
        case 'status': return item.message_feed_status_timestamp;
        case 'assignment': return item.message_feed_assignment_timestamp;
        default: return null;
    }
};

// Database feed timestamps have second-level precision. When a message and an
// automation action are created in the same second, keep the originating item
// ahead of the automation-generated item instead of relying on arrival order.
const compareFeedItemsChronologically = (itemA, itemB) => {
    const timestampDifference = getDateDifferenceInSeconds(
        getItemTimestamp(itemA),
        getItemTimestamp(itemB)
    );

    if (timestampDifference !== 0) {
        return timestampDifference;
    }

    const itemAIsAutomation = Boolean(itemA.message_feed_automation_title);
    const itemBIsAutomation = Boolean(itemB.message_feed_automation_title);
    const itemAIsOriginatingMessage = itemA.feedItemType === 'message' && !itemAIsAutomation;
    const itemBIsOriginatingMessage = itemB.feedItemType === 'message' && !itemBIsAutomation;

    if (itemAIsOriginatingMessage && itemBIsAutomation) {
        return -1;
    }

    if (itemAIsAutomation && itemBIsOriginatingMessage) {
        return 1;
    }

    return 0;
};

// stable identity for a feed item, mirroring the React keys used when rendering.
// Used to deduplicate the feed so a note/status/assignment cannot render twice.
const getFeedItemKey = (item) => {
    switch (item.feedItemType) {
        case 'message': return `message-${item.message_id}`;
        case 'note': return `note-${item.message_feed_note_id}`;
        case 'status': return `status-${item.message_feed_status_id}`;
        case 'assignment': return `assignment-${item.message_feed_assignment_id}`;
        default: return null;
    }
};

// remove duplicate feed items (same type + id), keeping the first occurrence.
const dedupeFeedItems = (items) => {
    const seen = new Set();
    const deduped = [];
    items.forEach(item => {
        const key = getFeedItemKey(item);
        // items without a resolvable key (e.g. pending messages) are always kept
        if (key === null) {
            deduped.push(item);
            return;
        }
        if (seen.has(key)) {
            return;
        }
        seen.add(key);
        deduped.push(item);
    });
    return deduped;
};

const isEnabledFlag = (value) => Number(value) === 1;

const formatFeedPhoneNumber = (phoneNumber) => formatPhoneNumber(phoneNumber) || phoneNumber;

function MessageFeed({
    organizationId,
    messageFeedId,
    updateSelectedMessageInList,
    onBack,
    onBackPressIn,
    inboxAiAvailable,
    grammarCheck,
    checkGrammar,
    clearGrammarCheck,
    auth,
}) {
    const {isDarkMode, theme} = useDarkMode();
    const {isMobile} = useDimensions();
    const [messageFeedState, setMessageFeedState] = useState({
        messageFeed: {},
        messageFeedItems: [],
        usersTyping: [],
    });
    const [paginationState, setPaginationState] = useState({
        currentPage: 0,
        hasMoreMessages: true,
        isLoadingMore: false,
        totalMessages: 0,
        lastMessageId: 0,
        lastNoteId: 0,
        lastStatusId: 0,
        lastAssignmentId: 0,
    });

    const paginationStateRef = useRef(paginationState);
    paginationStateRef.current = paginationState;
    const messageFeedItemsRef = useRef(messageFeedState.messageFeedItems);
    messageFeedItemsRef.current = messageFeedState.messageFeedItems;
    const [newMessageBody, setMessageBodyState] = useState('');
    const draftBody = useRef('');
    const setNewMessageBody = useCallback(value => {
        const next = typeof value === 'function' ? value(draftBody.current) : value;
        draftBody.current = next;
        setMessageBodyState(next);
    }, []);
    const [newMessageSubject, setNewMessageSubject] = useState('');
    const [webMessageInputHeight, setWebMessageInputHeight] = useState(22);
    const [newMessageAttachments, setNewMessageAttachments] = useState([]);
    const [recentlySentMessageId, setRecentlySentMessageId] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [grammarPreviewOpen, setGrammarPreviewOpen] = useState(false);
    const handledGrammarCheckId = useRef(null);
    const grammarStatus = grammarCheck?.status;
    const grammarChecking = grammarStatus === 'in_progress';
    const grammarReady = grammarStatus === 'complete' && typeof grammarCheck?.result?.text === 'string';
    const grammarRequestError = grammarStatus === 'error'
        ? grammarCheck?.error?.message || 'Grammar review failed. Please try again.'
        : null;
    const canApplyGrammar = grammarReady && !isSubmitting
        && [grammarCheck.originalText, grammarCheck.result.text, ''].includes(newMessageBody);
    const dismissGrammarReview = useCallback(() => {
        clearGrammarCheck(messageFeedId);
        setGrammarPreviewOpen(false);
    }, [clearGrammarCheck, messageFeedId]);

    const fixGrammar = useCallback(() => {
        if (isSubmitting || grammarChecking) return;
        if (grammarReady) {
            setGrammarPreviewOpen(true);
            return;
        }
        if (!inboxAiAvailable || !newMessageBody.trim()) return;
        checkGrammar({messageFeedId, text: newMessageBody});
    }, [checkGrammar, grammarChecking, grammarReady, inboxAiAvailable, isSubmitting, messageFeedId, newMessageBody]);

    useEffect(() => {
        if (!grammarReady || handledGrammarCheckId.current === grammarCheck.id) return;
        handledGrammarCheckId.current = grammarCheck.id;
        setGrammarPreviewOpen(true);
    }, [grammarReady, grammarCheck]);
    const fetchDataRequestSource = useRef(null);
    const resolveRequestSource = useRef(null);
    const loadMoreRequestSource = useRef(null);
    const fetchTimeout = useRef(null);
    const deliveryConfirmationTimeout = useRef(null);
    const fetchDataRef = useRef(null);
    const [blockModalActive, setBlockModalActive] = useState(false);
    const [noteModalOpen, setNoteModalOpen] = useState(false);
    const messageInputRef = useRef(null);
    const [emojisActive, setEmojisActive] = useState(false);
    const [customerInfoActive, setCustomerInfoActive] = useState(false);
    const [scriptModalActive, setScriptModalActive] = useState(false);
    const [selectedMessageForModal, setSelectedMessageForModal] = useState(null);
    const typingTimeout = useRef(null);
    const typingPollTimeout = useRef(null);
    const typingPollSource = useRef(null);
    const typingIdleTimeout = useRef(null);
    const isUserTypingRef = useRef(false);
    const typingRequestSource = useRef(null);
    const scrollViewHeightRef = useRef(null);
    const scrollContentHeightRef = useRef(0);
    const scrollOffsetRef = useRef(0);
    const prependScrollAnchorRef = useRef(null);
    const pendingMessageIdRef = useRef(0);
    const sentMessageTimeoutRef = useRef(null);

    const scrollViewRef = useRef(null);


    const updateWebMessageInputHeight = useCallback((target) => {
        if (Platform.OS !== 'web' || !target) {
            return;
        }

        target.style.height = 'auto';
        const nextHeight = Math.max(18, target.scrollHeight || 0);
        target.style.height = `${nextHeight}px`;
        setWebMessageInputHeight(nextHeight);
    }, []);

    const markPendingMessageDeliveryUnknown = useCallback((pendingMessageId) => {
        setMessageFeedState(prev => ({
            ...prev,
            messageFeedItems: prev.messageFeedItems.map(item => (
                item.is_pending_message && String(item.message_id) === String(pendingMessageId)
            ) ? {
                ...item,
                is_delivery_confirmation_delayed: true,
            } : item),
        }));
    }, []);

    const addPendingMessageToFeed = useCallback((messageBody, messageAttachments = []) => {
        const pendingMessageId = `pending-${Date.now()}-${pendingMessageIdRef.current++}`;
        const currentUser = auth?.user || {};
        const pendingMessage = {
            feedItemType: 'message',
            is_pending_message: true,
            message_id: pendingMessageId,
            message_bound_type: 'outbound',
            user_organization_id: currentUser.user_organization_id,
            profile_first_name: currentUser.profile_first_name,
            profile_last_name: currentUser.profile_last_name,
            user_avatar_file: currentUser.user_avatar_file,
            message_body: messageBody,
            message_created_timestamp: new Date().toISOString(),
            attachments: messageAttachments.map((attachment, index) => ({
                message_attachment_id: `${pendingMessageId}-attachment-${index}`,
                message_attachment_file_name: attachment,
                message_attachment_external_path: null,
            })),
        };

        setMessageFeedState(prev => ({
            ...prev,
            messageFeedItems: [...prev.messageFeedItems, pendingMessage],
        }));

        setTimeout(() => {
            if (scrollViewRef.current) {
                scrollViewRef.current.scrollToEnd({animated: true});
            }
        }, 0);

        return pendingMessageId;
    }, [auth]);

    const removePendingMessageFromFeed = useCallback((pendingMessageId) => {
        setMessageFeedState(prev => ({
            ...prev,
            messageFeedItems: prev.messageFeedItems.filter(item => (
                !item.is_pending_message
                || String(item.message_id) !== String(pendingMessageId)
            )),
        }));
    }, []);

    const updateMessageFeed = useCallback((messageFeed, messages, notes = [], statuses = [], assignments = [], scrollToBottom = false, usersTyping = []) => {
        updateSelectedMessageInList(messageFeed);
        const messageFeedItems = dedupeFeedItems([
            ...messages,
            ...notes,
            ...statuses,
            ...assignments,
        ]).sort(compareFeedItemsChronologically);


        setMessageFeedState({
            messageFeed,
            messageFeedItems,
            usersTyping: usersTyping.filter(v => v)
        });

        if(scrollToBottom && scrollViewRef.current) {
            scrollViewRef.current.scrollToEnd({animated: false});
        }
    }, [updateSelectedMessageInList]);

    const setupForSubmit = useCallback(() => {
        setIsSubmitting(true);
        if (fetchTimeout.current) {
            clearTimeout(fetchTimeout.current);
            fetchTimeout.current = null;
        }
    }, [])

    const loadPreviousMessages = useCallback(async () => {
        if (paginationState.isLoadingMore || !paginationState.hasMoreMessages) {
            return;
        }

        setPaginationState(prev => ({ ...prev, isLoadingMore: true }));

        // Cancel any existing load more request
        if (loadMoreRequestSource.current) {
            loadMoreRequestSource.current.cancel('Request Canceled');
        }

        // Cancel auto refresh while loading previous messages
        if (fetchDataRequestSource.current) {
            fetchDataRequestSource.current.cancel('Request Canceled');
        }
        clearTimeout(fetchTimeout.current);

        loadMoreRequestSource.current = CancelToken.source();

        try {
            const nextPage = paginationState.currentPage + 1;

            // Get the datetime of the oldest currently loaded message to know where to start fetching timeline items
            const oldestMessage = messageFeedState.messageFeedItems
                .filter(item => item.feedItemType === 'message')
                .sort((a, b) => new Date(a.message_created_timestamp) - new Date(b.message_created_timestamp))[0];

            const oldestMessageTime = oldestMessage ? oldestMessage.message_created_timestamp : null;

            const { messages, notes, statuses, assignments, totalMessages } = await MessagesService.getMessageFeedMessages(
                organizationId,
                messageFeedId,
                nextPage,
                MESSAGES_PER_PAGE,
                loadMoreRequestSource.current?.token,
                oldestMessageTime
            );

            setPaginationState(prev => {
                // Calculate if there are more messages based on total vs loaded
                const totalLoadedMessages = (nextPage + 1) * MESSAGES_PER_PAGE;
                const hasMoreMessages = totalLoadedMessages < totalMessages;

                return {
                    ...prev,
                    currentPage: nextPage,
                    hasMoreMessages,
                    totalMessages,
                    isLoadingMore: false
                };
            });

            // Create new timeline items array and sort them chronologically
            // The API already returns the timeline interval for this message page.
            // Keep leading events from the oldest page, including notes created
            // before the conversation's first message.
            const newTimelineItems = [
                ...messages,
                ...(notes || []),
                ...(statuses || []),
                ...(assignments || [])
            ]
                .sort(compareFeedItemsChronologically);

            // Prepend the previous page without discarding the messages already
            // in the timeline. Existing items win when page boundaries overlap.
            prependScrollAnchorRef.current = {
                contentHeight: scrollContentHeightRef.current,
                scrollOffset: scrollOffsetRef.current,
            };
            setMessageFeedState(prev => ({
                ...prev,
                messageFeedItems: dedupeFeedItems([
                    ...prev.messageFeedItems,
                    ...newTimelineItems,
                ]).sort(compareFeedItemsChronologically),
            }));

            // Restart auto refresh after loading previous messages
            fetchTimeout.current = setTimeout(() => {
                if (fetchDataRef.current) {
                    fetchDataRef.current(false, false, false);
                }
            }, 15000);

        } catch (e) {
            prependScrollAnchorRef.current = null;
            if (!e.__CANCEL__) {
                console.log('Error loading previous messages: ', e);
            }
            setPaginationState(prev => ({ ...prev, isLoadingMore: false }));

            // Restart auto refresh
            fetchTimeout.current = setTimeout(() => {
                if (fetchDataRef.current) {
                    fetchDataRef.current(false, false, false);
                }
            }, 15000);
        }
    }, [messageFeedId, messageFeedState.messageFeedItems, organizationId, paginationState.currentPage, paginationState.hasMoreMessages, paginationState.isLoadingMore]);

    const pollTyping = useCallback(async () => {
        if (Platform.OS === 'web' && typeof document !== 'undefined' && document.hidden) {
            typingPollTimeout.current = setTimeout(pollTyping, TYPING_POLL_INTERVAL_MS);
            return;
        }

        if (typingPollSource.current) {
            typingPollSource.current.cancel('Request Canceled');
        }
        typingPollSource.current = CancelToken.source();
        const source = typingPollSource.current;

        try {
            const { usersTyping } = await MessagesService.getMessageFeed(organizationId, messageFeedId, source.token, {typing: true});
            setMessageFeedState(prev => ({...prev, usersTyping: (usersTyping || []).filter(v => v)}));
        } catch(e) {
            if (!e.__CANCEL__) {
                console.log('Error polling typing: ', e);
            }
        } finally {
            // Don't reschedule after unmount / feed change.
            if (typingPollSource.current === source) {
                typingPollTimeout.current = setTimeout(pollTyping, TYPING_POLL_INTERVAL_MS);
            }
        }
    }, [messageFeedId, organizationId]);

    useEffect(() => {
        pollTyping();
        return () => {
            clearTimeout(typingPollTimeout.current);
            if (typingPollSource.current) {
                typingPollSource.current.cancel('Request Canceled');
            }
            typingPollSource.current = null;
        };
    }, [pollTyping]);

    const fetchData = useCallback(async (scrollToBottom = false, force = false, resetPagination = true) => {
        if (!isSubmitting || force) {
            if(fetchDataRequestSource.current) {
                fetchDataRequestSource.current.cancel('Request Canceled');
            }
            // create new cancel token
            fetchDataRequestSource.current = CancelToken.source();

            //Clear existing timeout
            clearTimeout(fetchTimeout.current);

            try {
                // Get message feed info and typing status
                const {messageFeed} = await MessagesService.getMessageFeed(
                    organizationId,
                    messageFeedId,
                    fetchDataRequestSource.current?.token,
                );

                if (resetPagination) {
                    // Initial load - get first page of messages with timeline data
                    const {messages, notes, statuses, assignments, totalMessages} = await MessagesService.getMessageFeedMessages(
                        organizationId,
                        messageFeedId,
                        0,
                        MESSAGES_PER_PAGE,
                        fetchDataRequestSource.current?.token
                    );

                    // For initial load, show all notes/statuses/assignments for the message feed
                    // No timestamp filtering needed since we want all relevant items
                    const filteredNotes = notes || [];
                    const filteredStatuses = statuses || [];
                    const filteredAssignments = assignments || [];


                    // Calculate if there are more messages based on total vs loaded
                    const hasMoreMessages = totalMessages > MESSAGES_PER_PAGE;

                    // Find the highest IDs from the loaded items
                    const maxMessageId = messages.length > 0 ? Math.max(...messages.map(m => m.message_id)) : 0;
                    const maxNoteId = filteredNotes.length > 0 ? Math.max(...filteredNotes.map(n => n.message_feed_note_id)) : 0;
                    const maxStatusId = filteredStatuses.length > 0 ? Math.max(...filteredStatuses.map(s => s.message_feed_status_id)) : 0;
                    const maxAssignmentId = filteredAssignments.length > 0 ? Math.max(...filteredAssignments.map(a => a.message_feed_assignment_id)) : 0;

                    setPaginationState(prev => ({
                        ...prev,
                        currentPage: 0,
                        hasMoreMessages,
                        totalMessages,
                        lastMessageId: maxMessageId,
                        lastNoteId: maxNoteId,
                        lastStatusId: maxStatusId,
                        lastAssignmentId: maxAssignmentId
                    }));

                    updateMessageFeed(messageFeed, messages, filteredNotes, filteredStatuses, filteredAssignments, scrollToBottom);
                } else {
                    // Incremental refresh - get new items and deduplicate

                    const {messages, notes, statuses, assignments} = await MessagesService.getMessageFeedMessages(
                        organizationId,
                        messageFeedId,
                        0,
                        MESSAGES_PER_PAGE,
                        fetchDataRequestSource.current?.token
                    );


                    // For incremental refresh, also get the current statuses from messageFeed
                    const currentStatuses = statuses || []

                    // Filter out items that are older than or equal to the last ID of each type
                    const newMessages = (messages || []).filter(msg => {
                        const isNewer = msg.message_id > paginationStateRef.current.lastMessageId;
                        return isNewer;
                    });

                    const newNotes = (notes || []).filter(note => {
                        return note.message_feed_note_id > paginationStateRef.current.lastNoteId;
                    });

                    // For incremental refresh, we need to preserve existing statuses and add new ones
                    // Get all current statuses from messageFeed and filter out duplicates
                    const allCurrentStatuses = currentStatuses.filter(status => {
                        return status.message_feed_status_id > paginationStateRef.current.lastStatusId;
                    });

                    const newAssignments = (assignments || []).filter(assignment => {
                        return assignment.message_feed_assignment_id > paginationStateRef.current.lastAssignmentId;
                    });


                    // Always update the last IDs, even if no new items
                    const newMaxMessageId = newMessages.length > 0 ? Math.max(...newMessages.map(m => m.message_id)) : paginationStateRef.current.lastMessageId;
                    const newMaxNoteId = newNotes.length > 0 ? Math.max(...newNotes.map(n => n.message_feed_note_id)) : paginationStateRef.current.lastNoteId;
                    const newMaxStatusId = allCurrentStatuses.length > 0 ? Math.max(...allCurrentStatuses.map(s => s.message_feed_status_id)) : paginationStateRef.current.lastStatusId;
                    const newMaxAssignmentId = newAssignments.length > 0 ? Math.max(...newAssignments.map(a => a.message_feed_assignment_id)) : paginationStateRef.current.lastAssignmentId;

                    // Combine new items with current statuses
                    const allNewItems = [...newMessages, ...newNotes, ...allCurrentStatuses, ...newAssignments];
                    const fetchedMessagesById = new Map(
                        (messages || []).map(message => [String(message.message_id), message])
                    );

                    setMessageFeedState(prev => {
                        const unmatchedPersistedMessages = [...newMessages]
                            .sort((messageA, messageB) => messageA.message_id - messageB.message_id);
                        const confirmedPendingMessageIds = new Set();

                        prev.messageFeedItems
                            .filter(item => item.is_pending_message)
                            .forEach(pendingMessage => {
                                const matchIndex = unmatchedPersistedMessages.findIndex(message => {
                                    return doesMessageConfirmPendingDelivery(pendingMessage, message);
                                });

                                if (matchIndex !== -1) {
                                    confirmedPendingMessageIds.add(String(pendingMessage.message_id));
                                    unmatchedPersistedMessages.splice(matchIndex, 1);
                                }
                            });
                        const refreshedItems = prev.messageFeedItems.filter(item => {
                            return !confirmedPendingMessageIds.has(String(item.message_id));
                        }).map(item => {
                            if (item.feedItemType !== 'message') {
                                return item;
                            }

                            return fetchedMessagesById.get(String(item.message_id)) || item;
                        });
                        // dedupe every item type (not just messages) against what is
                        // already in the feed so notes/statuses/assignments cannot be
                        // appended twice on a refresh.
                        const existingKeys = new Set(
                            refreshedItems.map(getFeedItemKey).filter(key => key !== null)
                        );
                        const itemsToAppend = allNewItems.filter(item => {
                            const key = getFeedItemKey(item);
                            return key === null || !existingKeys.has(key);
                        });

                        return {
                            ...prev,
                            messageFeed,
                            messageFeedItems: [...refreshedItems, ...itemsToAppend]
                                .sort(compareFeedItemsChronologically),
                        };
                    });

                    if (allNewItems.length > 0) {
                        setTimeout(() => {
                            if(scrollViewRef.current) {
                                scrollViewRef.current.scrollToEnd({animated: true});
                            }
                        })
                    }

                    // Always update pagination state with current last IDs
                    setPaginationState(prev => ({
                        ...prev,
                        lastMessageId: newMaxMessageId,
                        lastNoteId: newMaxNoteId,
                        lastStatusId: newMaxStatusId,
                        lastAssignmentId: newMaxAssignmentId
                    }));
                }

                // Incremental refreshes merge page zero into the existing timeline,
                // so polling can continue while the user is reading older messages.
                fetchTimeout.current = setTimeout(() => {
                    fetchData(false, false, false);
                }, 15000);
            } catch (e) {
                clearTimeout(fetchTimeout.current);
                if (!e.__CANCEL__) {
                    console.log('Error fetching message feeds: ', e);
                    fetchTimeout.current = setTimeout(() => {
                        fetchData(false, false, false);
                    }, 15000);
                }
            }
        }
    }, [isSubmitting, messageFeedId, organizationId, updateMessageFeed]);

    useEffect(() => {
        fetchDataRef.current = fetchData;
    }, [fetchData]);

    const updateFeedWithExistingData = useCallback((messageFeed, messages, addNotes = [], addStatuses = [], addAssignments = [], removePendingMessageId = null) => {

        const existingNotes = [];
        const existingStatuses = [];
        const existingAssignments = [];
        const existingPendingMessages = [];

        messageFeedItemsRef.current.forEach(item => {
            if (item.is_pending_message) {
                if (String(item.message_id) !== String(removePendingMessageId)) {
                    existingPendingMessages.push(item);
                }
                return;
            }

            switch (item.feedItemType) {
                case 'note':
                    existingNotes.push(item);
                    break;
                case 'status':
                    existingStatuses.push(item);
                    break;
                case 'assignment':
                    existingAssignments.push(item);
                    break;
                default:
                    break;
            }
        });


        const allNotes = [...existingNotes, ...addNotes];
        const allStatuses = [...existingStatuses, ...addStatuses];
        const allAssignments = [...existingAssignments, ...addAssignments];

        updateMessageFeed(messageFeed, [...messages, ...existingPendingMessages], allNotes, allStatuses, allAssignments, true);
    }, [updateMessageFeed]);

    const setFeedTyping = useCallback((typing) => {
        if (typingRequestSource.current) {
            typingRequestSource.current.cancel('Request Canceled');
        }
        // create new cancel token
        typingRequestSource.current = CancelToken.source();

        MessagesService.setUserTyping(organizationId, messageFeedId, typing, typingRequestSource.current?.token)
            .then(() => {
                if(typing && isUserTypingRef.current) {
                    // Refresh typing every 15s
                    typingTimeout.current = setTimeout(function() {setFeedTyping(true);}, 15000);
                }
            })
            .catch(e => {
                clearTimeout(typingTimeout.current);
                if (!e.__CANCEL__) {
                    console.log('Error setting typing: ', e);
                }
            })
    }, [messageFeedId, organizationId]);

    const resolveMessageFeed = useCallback(() => {
        if (!isSubmitting) {
            setupForSubmit()

            if(fetchDataRequestSource.current) {
                fetchDataRequestSource.current.cancel('Request Canceled');
            }

            if(resolveRequestSource.current) {
                resolveRequestSource.current.cancel('Request Canceled');
            }

            //Clear existing timeout
            clearTimeout(fetchTimeout.current);

            // Immediately update the resolved status
            const currentResolved = messageFeedState.messageFeed.message_feed_resolved;
            const newResolved = isEnabledFlag(currentResolved) ? 0 : 1;

            setMessageFeedState(prev => ({
                ...prev,
                messageFeed: {
                    ...prev.messageFeed,
                    message_feed_resolved: newResolved
                }
            }));

            resolveRequestSource.current = CancelToken.source();

            MessagesService.resolveMessageFeed(organizationId, messageFeedId, resolveRequestSource.current?.token)
                .then(({messageFeed, messages, updatedStatus}) => {
                    setIsSubmitting(false);
                    // add the new status to existing state
                    if (updatedStatus) {
                        const statusId = Number(updatedStatus.message_feed_status_id) || 0;

                        paginationStateRef.current = {
                            ...paginationStateRef.current,
                            lastStatusId: Math.max(paginationStateRef.current.lastStatusId, statusId),
                        };
                        setPaginationState(prev => ({
                            ...prev,
                            lastStatusId: Math.max(prev.lastStatusId, statusId),
                        }));

                        setMessageFeedState(prev => ({
                            ...prev,
                            messageFeed: messageFeed,
                            messageFeedItems: prev.messageFeedItems.some(item =>
                                item.feedItemType === 'status'
                                && String(item.message_feed_status_id) === String(updatedStatus.message_feed_status_id)
                            ) ? prev.messageFeedItems : [...prev.messageFeedItems, updatedStatus]
                        }));
                    }
                    fetchTimeout.current = setTimeout(() => fetchData(false, false, false), 5000);
                })
                .catch(e => {
                    setIsSubmitting(false);
                    if (!e.__CANCEL__) {
                        console.log('Error fetching resolving message feed: ', e);
                        // Revert the optimistic update on error
                        setMessageFeedState(prev => ({
                            ...prev,
                            messageFeed: {
                                ...prev.messageFeed,
                                message_feed_resolved: currentResolved
                            }
                        }));
                        fetchTimeout.current = setTimeout(() => fetchData(false, false, false), 5000);
                    }
                });
        }

    }, [fetchData, isSubmitting, messageFeedId, messageFeedState.messageFeed.message_feed_resolved, organizationId, setupForSubmit]);

    const toggleMessageFeedBlocked = useCallback(() => {
        if (!isSubmitting) {
            setupForSubmit()

            if(fetchDataRequestSource.current) {
                fetchDataRequestSource.current.cancel('Request Canceled');
            }

            //Clear existing timeout
            clearTimeout(fetchTimeout.current);

            MessagesService.toggleMessageFeedBlocked(organizationId, messageFeedId)
                .then(({messageFeed, messages}) => {
                    setIsSubmitting(false);
                    updateFeedWithExistingData(messageFeed, messages);
                    fetchTimeout.current = setTimeout(() => fetchData(false, false, false), 5000);
                })
                .catch(e => {
                    setIsSubmitting(false);
                    if (!e.__CANCEL__) {
                        console.log('Error toggling message feed blocked status: ', e);
                        fetchTimeout.current = setTimeout(() => fetchData(false, false, false), 5000);
                    }
                });
        }

    }, [fetchData, isSubmitting, messageFeedId, organizationId, setupForSubmit, updateFeedWithExistingData]);

    const submitMessageFeedNote = useCallback((note) => {
        if (!isSubmitting) {
            setupForSubmit()

            if(fetchDataRequestSource.current) {
                fetchDataRequestSource.current.cancel('Request Canceled');
            }

            //Clear existing timeout
            clearTimeout(fetchTimeout.current);

            MessagesService.addMessageFeedNote(organizationId, messageFeedId, note)
                .then(({messageFeed, messages}) => {
                    let addNote = [];
                    if (messageFeed.notes && messageFeed.notes.length > 0) {
                        // notes are returned newest-first, so pick the highest id
                        // (the note we just created) rather than the last array item,
                        // which is the OLDEST note and is already in the feed.
                        const newestNote = messageFeed.notes.reduce((latest, current) =>
                            Number(current.message_feed_note_id) > Number(latest.message_feed_note_id) ? current : latest
                        );
                        addNote = [newestNote];

                        // advance the note high-water mark so the next poll doesn't re-append it
                        const newestNoteId = Number(newestNote.message_feed_note_id) || 0;
                        paginationStateRef.current = {
                            ...paginationStateRef.current,
                            lastNoteId: Math.max(paginationStateRef.current.lastNoteId, newestNoteId),
                        };
                        setPaginationState(prev => ({
                            ...prev,
                            lastNoteId: Math.max(prev.lastNoteId, newestNoteId),
                        }));
                    }

                    setIsSubmitting(false);
                    updateFeedWithExistingData(messageFeed, messages, addNote);
                    fetchTimeout.current = setTimeout(fetchData, 5000);
                });
        }

    }, [fetchData, isSubmitting, messageFeedId, organizationId, setupForSubmit, updateFeedWithExistingData]);

    const submitNewMessage = useCallback(() => {
        if (!isSubmitting) {
            const outgoingMessageBody = newMessageBody;
            const outgoingMessageAttachments = [...newMessageAttachments];
            const outgoingSubject = newMessageSubject || null;
            const isEmailFeed = Boolean(messageFeedState.messageFeed.message_feed_email_address);

            setupForSubmit();
            const pendingMessageId = addPendingMessageToFeed(
                outgoingMessageBody,
                outgoingMessageAttachments
            );
            setNewMessageBody('');
            clearGrammarCheck(messageFeedId);
            setGrammarPreviewOpen(false);
            setWebMessageInputHeight(22);
            setNewMessageAttachments([]);

            if(fetchDataRequestSource.current) {
                fetchDataRequestSource.current.cancel('Request Canceled');
            }

            //Clear existing timeout
            clearTimeout(fetchTimeout.current);

            clearTimeout(deliveryConfirmationTimeout.current);
            if (!isEmailFeed) {
                deliveryConfirmationTimeout.current = setTimeout(() => {
                    markPendingMessageDeliveryUnknown(pendingMessageId);
                    clearTimeout(fetchTimeout.current);
                    fetchTimeout.current = setTimeout(() => {
                        fetchData(false, true, false);
                    }, 0);
                }, DELIVERY_CONFIRMATION_TIMEOUT_MS);
            }

            MessagesService.sendNewMessage(organizationId, messageFeedId, outgoingMessageBody, outgoingMessageAttachments, outgoingSubject)
                .then(({messageFeed, messages, deliveryUnknown}) => {
                    setIsSubmitting(false);

                    if (deliveryUnknown) {
                        markPendingMessageDeliveryUnknown(pendingMessageId);
                        setMessageFeedState(prev => ({
                            ...prev,
                            messageFeed,
                        }));
                        return;
                    }

                    const lastOutboundMessage = [...messages]
                        .filter(message => message.message_bound_type === 'outbound' && message.message_body === outgoingMessageBody)
                        .sort((a, b) => b.message_id - a.message_id)[0]
                        || [...messages]
                        .filter(message => message.message_bound_type === 'outbound')
                        .sort((a, b) => b.message_id - a.message_id)[0];

                    if (lastOutboundMessage?.message_id) {
                        setRecentlySentMessageId(lastOutboundMessage.message_id);
                        clearTimeout(sentMessageTimeoutRef.current);
                        sentMessageTimeoutRef.current = setTimeout(() => {
                            setRecentlySentMessageId(null);
                        }, 8000);
                    }

                    const maxMessageId = messages.length > 0
                    ? Math.max(...messages.map(message => message.message_id))
                    : paginationStateRef.current.lastMessageId;

                    paginationStateRef.current = {
                        ...paginationStateRef.current,
                        lastMessageId: Math.max(
                            paginationStateRef.current.lastMessageId,
                            maxMessageId
                        )
                    };

                    setPaginationState(prev => ({
                        ...prev,
                        lastMessageId: Math.max(
                            prev.lastMessageId,
                            maxMessageId
                        )
                    }));

                    updateFeedWithExistingData(messageFeed, messages, [], [], [], pendingMessageId);
                }).catch((e) => {
                    setIsSubmitting(false);
                    if (!e.__CANCEL__) {
                        if (isEmailFeed) {
                            removePendingMessageFromFeed(pendingMessageId);
                        } else {
                            markPendingMessageDeliveryUnknown(pendingMessageId);
                        }
                    }
                    // noinspection JSUnresolvedReference
                    if (!e.__CANCEL__) {
                        console.log('Error submitting new message: ', e);
                    }
                }).finally(() => {
                    clearTimeout(deliveryConfirmationTimeout.current);
                    clearTimeout(fetchTimeout.current);
                    fetchTimeout.current = setTimeout(() => {
                        fetchData(false, true, false);
                    }, 5000);
                    if(emojisActive) {
                        setEmojisActive(false);
                    }
            });
        }

    }, [addPendingMessageToFeed, clearGrammarCheck, emojisActive, fetchData, isSubmitting, markPendingMessageDeliveryUnknown, messageFeedId, messageFeedState.messageFeed.message_feed_email_address, newMessageAttachments, newMessageBody, newMessageSubject, organizationId, removePendingMessageFromFeed, setNewMessageBody, setupForSubmit, updateFeedWithExistingData]);

    const handleAttachmentImageUpload = useCallback(async (results) => {
        let file = results[0];
        let formData = new FormData();
        formData.append('image', file);

        let fileName = null;
        try {
            fileName = await MessagesService.uploadImage(organizationId, formData);
            setNewMessageAttachments(current => {
                return [
                    ...current,
                    fileName
                ];
            });
        } catch (error) {
            console.log('uploadError: ', error);
        }
    }, [organizationId]);

    const removeAttachment = useCallback((attachment) => {
        setNewMessageAttachments(current => {
            const attachmentIndex = current.indexOf(attachment);
            current.splice(attachmentIndex, 1);

            return [...current];
        })
    }, [])

    const getNoteByText = useCallback((messageFeedItem) => {
        let noteByText = 'Note by ';

        // Automations will set a note by name.
        if (messageFeedItem.message_feed_note_created_by_name) {
            noteByText += messageFeedItem.message_feed_note_created_by_name;
        } else {
            noteByText += `${[messageFeedItem.profile_first_name, messageFeedItem.profile_last_name].filter(Boolean).join(' ')}`;
        }

        if(messageFeedItem.message_feed_automation_title) {
            noteByText += ` Automation ${messageFeedItem.message_feed_automation_title}`;
        }

        noteByText +=  ' on ' + formatDate(messageFeedItem.message_feed_note_timestamp) + '@' + formatDate(messageFeedItem.message_feed_note_timestamp, 'hh:mm a');

        return noteByText;
    }, [])

    const getAssignedByText = useCallback((messageFeedItem) => {
        let assignedByText = 'Assigned to ';

        assignedByText += `${[messageFeedItem.profile_first_name, messageFeedItem.profile_last_name].filter(Boolean).join(' ')}`;

        if(messageFeedItem.message_feed_automation_title) {
            assignedByText += ` via Fluid Automation ${messageFeedItem.message_feed_automation_title}`;
        } else {
            assignedByText += messageFeedItem.message_feed_assignment_reason ? ` via ${messageFeedItem.message_feed_assignment_reason}`  : '';
        }

        assignedByText += ` on ${formatDate(messageFeedItem.message_feed_assignment_timestamp)} @ ${formatDate(messageFeedItem.message_feed_assignment_timestamp, 'hh:mm a')}`;
        return assignedByText;
    })

    const getResolvedByText = useCallback((messageFeedItem) => {
        let resolvedByText = isEnabledFlag(messageFeedItem.message_feed_resolved) ? 'Resolved ' : 'Reopened ';
        resolvedByText += 'by ';

        // Automations will set a resolved by name.
        if (messageFeedItem.message_feed_resolved_by_name) {
            resolvedByText += messageFeedItem.message_feed_resolved_by_name;
        } else if (messageFeedItem.profile_first_name || messageFeedItem.profile_last_name) {
            resolvedByText += [messageFeedItem.profile_first_name, messageFeedItem.profile_last_name].filter(Boolean).join(' ');
        } else {
            // if the item was resolved automatically, name will be null.
            if (isEnabledFlag(messageFeedItem.message_feed_resolved)) {
                resolvedByText += 'Fluid Services'
            } else {
                // if the item was reopened automatically use feed phone number.
                resolvedByText += formatFeedPhoneNumber(messageFeedItem.message_feed_phone_number);
            }
        }

        if (messageFeedItem.message_feed_automation_title) {
            resolvedByText +=
                ` Automation ${messageFeedItem.message_feed_automation_title}`;
        }

        resolvedByText += ' on ' + formatDate(messageFeedItem.message_feed_status_timestamp) + ' @ ' + formatDate(messageFeedItem.message_feed_status_timestamp, 'hh:mm a');

        return resolvedByText;
    }, [])

    useEffect(() => {
        setMessageFeedState({
            messageFeed: {},
            messageFeedItems: [],
            usersTyping: []
        });
        setWebMessageInputHeight(22);
        setNewMessageSubject('');
        setRecentlySentMessageId(null);
        scrollContentHeightRef.current = 0;
        scrollOffsetRef.current = 0;
        prependScrollAnchorRef.current = null;
        setPaginationState({
            currentPage: 0,
            hasMoreMessages: true,
            isLoadingMore: false,
            totalMessages: 0,
            lastMessageId: 0,
            lastNoteId: 0,
            lastStatusId: 0,
            lastAssignmentId: 0,
        });
        setIsSubmitting(false);
        if (fetchDataRef.current) {
            fetchDataRef.current(true, true);
        }
        return () => {
            setIsSubmitting(false);
            if(fetchDataRequestSource.current) {
                fetchDataRequestSource.current.cancel();
            }
            if(resolveRequestSource.current) {
                resolveRequestSource.current.cancel();
            }
            if(loadMoreRequestSource.current) {
                loadMoreRequestSource.current.cancel();
            }
            clearTimeout(fetchTimeout.current);
            clearTimeout(deliveryConfirmationTimeout.current);
            clearTimeout(typingTimeout.current);
            clearTimeout(typingIdleTimeout.current);
            isUserTypingRef.current = false;
            clearTimeout(sentMessageTimeoutRef.current);
        };
    }, [messageFeedId, organizationId]);

    useEffect(() => {
        const hasTypingActivity = newMessageBody.length > 0 || newMessageAttachments.length > 0
            || emojisActive || scriptModalActive || noteModalOpen;

        clearTimeout(typingIdleTimeout.current);

        if (hasTypingActivity && !isUserTypingRef.current) {
            isUserTypingRef.current = true;
            setFeedTyping(true);
        }

        if (!isUserTypingRef.current) {
            return;
        }

        // Each edit resets this timer, so continuous typing stays active while pauses expire.
        typingIdleTimeout.current = setTimeout(() => {
            isUserTypingRef.current = false;
            clearTimeout(typingTimeout.current);
            setFeedTyping(false);
        }, 5000);
    }, [emojisActive, newMessageAttachments, newMessageBody, noteModalOpen, scriptModalActive, setFeedTyping]);

    useEffect(() => {
        if (Platform.OS === 'web' && messageInputRef.current) {
            updateWebMessageInputHeight(messageInputRef.current);
        }
    }, [newMessageBody, updateWebMessageInputHeight]);

    useEffect(() => {
        if (messageFeedState.messageFeed.message_feed_email_address && messageFeedState.messageFeed.message_feed_email_subject && !newMessageSubject) {
            setNewMessageSubject(messageFeedState.messageFeed.message_feed_email_subject);
        }
    }, [messageFeedState.messageFeed.message_feed_email_subject, messageFeedState.messageFeed.message_feed_email_address]);

    const getMessageDeliveryStatus = useCallback((status) => {
        if (status === 'blocked') {
            return 'Customer opted out of SMS messages';
        }

        if (status === 'unreachable' || status === 'failed' || status === 'undelivered') {
            return 'Customer unreachable by SMS';
        }
        return null;
    }, []);

    if (messageFeedState.messageFeed.message_feed_id) {
        return (
            <View style={[styles.messageFeedWrapper]}>
                <View style={[styles.messageFeedContainer, getThemeTransitionStyle(), isDarkMode && {
                    backgroundColor: theme.background,
                    borderRightColor: theme.border,
                }]}>
                    <TouchableOpacity
                        style={[styles.messageFeedStatusContainer, (isEnabledFlag(messageFeedState.messageFeed.message_feed_resolved) ? styles.messageFeedResolved : {})]}
                        onPress={resolveMessageFeed}
                        disabled={isSubmitting}
                    >
                        <AppText style={[styles.messageFeedStatusText]}>
                            {
                                isEnabledFlag(messageFeedState.messageFeed.message_feed_resolved) ?
                                    'This conversation has been resolved'
                                    :
                                    'Mark this task as resolved'
                            }
                        </AppText>
                        {isSubmitting ? (
                            <View style={styles.resolvedIndicatorContainer}>
                                <ActivityIndicator size="small" color="#FFFFFF" />
                            </View>
                        ) : (
                            isEnabledFlag(messageFeedState.messageFeed.message_feed_resolved) ?
                                <MessageFeedResolvedSvg color="#FFFFFF"/>
                                :
                                <MessageFeedUnresolvedSvg color="#FFFFFF"/>
                        )}
                    </TouchableOpacity>
                    <View style={[styles.customerInfoTop, getThemeTransitionStyle(), isDarkMode && {
                        borderBottomColor: theme.border,
                    }]}>
                        <TouchableOpacity
                            style={styles.mobileBackToListAction}
                            onPressIn={() => {
                                if (onBackPressIn) {
                                    onBackPressIn();
                                }
                            }}
                            onPress={() => {
                                if (onBack) {
                                    onBack();
                                } else {
                                    navigate('MessageFlow', {messageFeedId: null});
                                }
                            }}
                        >
                            <ChevronLeftBlueSvg/>
                        </TouchableOpacity>
                        <Avatar
                            source={{uri: AVATAR_MESSENGER}}
                            width={40}
                            style={{marginRight: 20}}
                        />
                        <View style={[basicStyles.flexScale]}>
                            <TextInput
                                style={[styles.contactNameTextInput, isDarkMode && {color: theme.text}]}
                                placeholder="Contact Name"
                                placeholderTextColor={isDarkMode ? theme.textSecondary : undefined}
                                value={messageFeedState.messageFeed.message_feed_contact_name || ''}
                                disabled={true}
                            />
                            <AppText style={[styles.contactPhoneNumber, isDarkMode && {color: theme.textSecondary, textDecorationColor: theme.textSecondary}]}>
                                {messageFeedState.messageFeed.message_feed_phone_number
                                    ? formatFeedPhoneNumber(messageFeedState.messageFeed.message_feed_phone_number)
                                    : messageFeedState.messageFeed.message_feed_email_address}
                            </AppText>
                        </View>
                        <TouchableOpacity onPress={() => {
                            setCustomerInfoActive(true);
                        }}>
                            <InfoSvg/>
                        </TouchableOpacity>
                    </View>
                    {messageFeedState.messageFeedItems.some(item => item.feedItemType === 'message' && item.message_feed_email_address && item.message_body_html) && (
                        <View style={[styles.showImagesContainer, getThemeTransitionStyle(), isDarkMode && {borderBottomColor: theme.border}]}>
                            <TouchableOpacity
                                style={[styles.showImagesButton, isDarkMode && {backgroundColor: theme.inputBg, borderColor: theme.border}]}
                                onPress={async () => {
                                    try {
                                        const showImages = !isEnabledFlag(messageFeedState.messageFeed.message_feed_show_html_images);
                                        await MessagesService.updateMessageFeedShowHtmlImages(organizationId, messageFeedId, showImages);

                                        setMessageFeedState(prev => ({
                                            ...prev,
                                            messageFeed: {
                                                ...prev.messageFeed,
                                                message_feed_show_html_images: showImages ? 1 : 0
                                            }
                                        }));
                                    } catch (e) {
                                        console.log('Error updating show html images: ', e);
                                    }
                                }}
                            >
                                <EyeSvg fill={isDarkMode ? '#FFFFFF' : '#0065DB'} />
                                <AppText style={[styles.showImagesText, isDarkMode && {color: '#FFFFFF'}]}>
                                    {isEnabledFlag(messageFeedState.messageFeed.message_feed_show_html_images) ? 'Hide Images' : 'Show Images'}
                                </AppText>
                            </TouchableOpacity>
                        </View>
                    )}
                    <ScrollView
                        ref={scrollViewRef}
                        style={[styles.messagesContainer]}
                        onScroll={(event) => {
                            scrollOffsetRef.current = event.nativeEvent.contentOffset.y;
                        }}
                        scrollEventThrottle={16}
                        onContentSizeChange={(width, height) => {
                            scrollContentHeightRef.current = height;

                            const anchor = prependScrollAnchorRef.current;
                            if (anchor && scrollViewRef.current) {
                                const addedHeight = Math.max(0, height - anchor.contentHeight);
                                scrollViewRef.current.scrollTo({
                                    y: anchor.scrollOffset + addedHeight,
                                    animated: false,
                                });
                                prependScrollAnchorRef.current = null;
                            }
                        }}
                        onLayout={(event) => {
                            const { height } = event.nativeEvent.layout;
                            scrollViewHeightRef.current = height;
                        }}
                        contentContainerStyle={(() => {
                            const messagesOnly = messageFeedState.messageFeedItems.filter(item => item.feedItemType === 'message');
                            const isSingleHtmlMessage = messagesOnly.length === 1 &&
                                !!messagesOnly[0].message_feed_email_address &&
                                !!messagesOnly[0].message_body_html;

                            if (isSingleHtmlMessage && scrollViewHeightRef.current) {
                                // Don't set minHeight here - let the inner View handle it
                                const style = {
                                    ...styles.messagesContainerExpanded
                                };
                                return style;
                            }
                            return undefined;
                        })()}>
                        {(() => {
                            return paginationState.hasMoreMessages;
                        })() && (
                            <View style={[styles.loadMoreContainer]}>
                                <TouchableOpacity
                                    style={[styles.loadMoreButton, isDarkMode && {backgroundColor: theme.loadMoreBg, borderColor: theme.loadMoreBorder}]}
                                    onPress={loadPreviousMessages}
                                    disabled={paginationState.isLoadingMore}
                                >
                                    {paginationState.isLoadingMore ? (
                                        <ActivityIndicator size="small" color="#0065DB" />
                                    ) : (
                                        <AppText style={[styles.loadMoreText, isDarkMode && {color: theme.text}]}>
                                            Previous Messages
                                        </AppText>
                                    )}
                                </TouchableOpacity>
                            </View>
                        )}
                        <View style={(() => {
                            const messagesOnly = messageFeedState.messageFeedItems.filter(item => item.feedItemType === 'message');
                            const isSingleHtmlMessage = messagesOnly.length === 1 &&
                                !!messagesOnly[0].message_feed_email_address &&
                                !!messagesOnly[0].message_body_html;
                            if (isSingleHtmlMessage && scrollViewHeightRef.current) {
                                // Account for 24px padding on top and bottom (48px total)
                                const contentHeight = scrollViewHeightRef.current - 48;
                                return {
                                    flex: 1,
                                    minHeight: contentHeight,
                                    height: contentHeight,
                                    maxHeight: contentHeight
                                };
                            }
                            return {};
                        })()}>
                        {
                            messageFeedState.messageFeedItems.map((messageFeedItem) => {
                                if(messageFeedItem.feedItemType === 'message') {
                                    const type = messageFeedItem.message_bound_type;
                                    const isInbound = type === 'inbound';
                                    const isOutbound = type === 'outbound';
                                    const isPendingMessage = Boolean(messageFeedItem.is_pending_message);
                                    const deliveryStatusReason = getMessageDeliveryStatus(messageFeedItem.message_delivery_status);
                                    const isUnreachable = isOutbound && Boolean(deliveryStatusReason);
                                    const typeStyles = isInbound ? inboundStyles : outboundStyles;
                                    let messageSender;
                                    let avatarUri = AVATAR_MESSENGER;
                                    if (isInbound) {
                                        messageSender = messageFeedState.messageFeed.message_feed_contact_name ?
                                            messageFeedState.messageFeed.message_feed_contact_name
                                            : messageFeedState.messageFeed.message_feed_phone_number_formatted
                                                ? formatPhoneNumber(messageFeedState.messageFeed.message_feed_phone_number_formatted)
                                                : messageFeedState.messageFeed.message_feed_email_address;
                                    } else if (isOutbound) {
                                        if(messageFeedItem.message_feed_automation_title) {
                                            messageSender = `Fluid Automation ${messageFeedItem.message_feed_automation_title}`
                                        }
                                        else if (isPendingMessage || messageFeedItem.user_organization_id) {
                                            messageSender = [messageFeedItem.profile_first_name, messageFeedItem.profile_last_name].filter(Boolean).join(' ')
                                                || messageFeedState.messageFeed.organization_title;
                                            avatarUri = messageFeedItem.user_avatar_file ? AVATAR_PATH_USER + messageFeedItem.user_avatar_file : AVATAR_MESSENGER;
                                        } else {
                                            messageSender = messageFeedState.messageFeed.organization_title;
                                            avatarUri = messageFeedState.messageFeed.organization_thumbnail ? AVATAR_PATH_ORG + messageFeedState.messageFeed.organization_thumbnail : AVATAR_MESSENGER;
                                        }
                                    }

                                    // Check if there's only one message and it's an HTML message
                                    const messagesOnly = messageFeedState.messageFeedItems.filter(item => item.feedItemType === 'message');
                                    const isSingleHtmlMessage = messagesOnly.length === 1 &&
                                        !!messageFeedItem.message_feed_email_address &&
                                        !!messageFeedItem.message_body_html;

                                    let messageContainerStyle = {};
                                    if (messageFeedItem.message_feed_email_address && messageFeedItem.message_body_html) {
                                        messageContainerStyle = {
                                            width: '100%',
                                        }
                                    }

                                    // If single HTML message, make the message expand to fill available height
                                    let messageSlotStyle = [styles.messageSlot, typeStyles.messageSlot];
                                    let messageWrapperStyle = [styles.messageWrapper, messageContainerStyle];
                                    if (isSingleHtmlMessage && scrollViewHeightRef.current) {
                                        // Account for 24px padding on top and bottom (48px total)
                                        const contentHeight = scrollViewHeightRef.current - 48;
                                        messageSlotStyle.push(styles.messageSlotExpanded, { height: contentHeight, minHeight: contentHeight });
                                        messageWrapperStyle.push(styles.messageWrapperExpanded);
                                    }

                                    return (
                                        <View key={`message-${messageFeedItem.message_id}`}
                                              style={messageSlotStyle}>
                                            <View style={messageWrapperStyle}>
                                                <View style={[styles.messageInnerWrapper, isSingleHtmlMessage && styles.messageInnerWrapperExpanded]}>
                                                    {
                                                        isInbound ?
                                                            <Avatar
                                                                source={{uri: avatarUri}}
                                                                width={40}
                                                                style={{marginRight: 20}}
                                                            />
                                                            : null
                                                    }
                                                    <View style={[styles.messageContentWrapper, isSingleHtmlMessage && styles.messageContentWrapperExpanded]}>
                                                        <View
                                                            style={[styles.messageBodyWrapper, typeStyles.messageBodyWrapper, isSingleHtmlMessage && styles.messageBodyWrapperExpanded,
                                                                isDarkMode && {backgroundColor: isInbound ? theme.messageBgInbound : theme.messageBgOutbound},
                                                                isUnreachable && styles.unreachableMessageBody,
                                                            ]}>
                                                            {messageFeedItem.message_subject && (
                                                                <>
                                                                <View style={[
                                                                    styles.messageSubjectHeaderContainer,
                                                                ]}>

                                                                    <AppText style={[
                                                                        styles.messageText,
                                                                        typeStyles.messageText,
                                                                        isDarkMode && {color: isInbound ? theme.messageTextInbound : theme.messageTextOutbound},
                                                                    ]}>
                                                                        {messageFeedItem.message_subject}
                                                                    </AppText>

                                                                    {messageFeedItem.message_feed_email_address && messageFeedItem.message_body_html && (
                                                                        <TouchableOpacity
                                                                            style={[styles.viewFullMessageButton]}
                                                                            onPress={() => {
                                                                                setSelectedMessageForModal(messageFeedItem);
                                                                            }}
                                                                        >
                                                                        <ExpandSvg height={24} width={24} circleFill={isDarkMode ? 'rgba(255,255,255,0.1)' : '#f3f5fa'} arrowFill={isDarkMode ? '#FFFFFF' : '#023d83'} />
                                                                        </TouchableOpacity>
                                                                    )}

                                                                </View>
                                                                    <View style={[
                                                                        styles.subjectSeparator,
                                                                        typeStyles.subjectSeparator,
                                                                        isDarkMode && {backgroundColor: isInbound ? theme.subjectSeparatorInbound : theme.subjectSeparatorOutbound},
                                                                    ]}/>
                                                                </>
                                                            )}


                                                            {isOutbound && deliveryStatusReason && (
                                                                <>
                                                                    <View style={[
                                                                        styles.messageSubjectHeaderContainer,
                                                                    ]}>

                                                                        <AppText style={[
                                                                            styles.messageText,
                                                                            typeStyles.messageText,
                                                                            isDarkMode && {color: isInbound ? theme.messageTextInbound : theme.messageTextOutbound},
                                                                            {marginBottom: 4}
                                                                        ]}>
                                                                            <View style={styles.errorIndicatorContainer}><AppText>!</AppText></View>
                                                                            {deliveryStatusReason}
                                                                        </AppText>

                                                                    </View>
                                                                    <View style={[
                                                                        styles.subjectSeparator,
                                                                        typeStyles.subjectSeparator,
                                                                        isDarkMode && {backgroundColor: isInbound ? theme.subjectSeparatorInbound : theme.subjectSeparatorOutbound},
                                                                    ]}/>
                                                                </>
                                                            )}


                                                            {messageFeedItem.message_feed_email_address && messageFeedItem.message_body_html ? (
                                                                <>
                                                                    {!messageFeedItem.message_subject && (
                                                                        <TouchableOpacity
                                                                            style={[styles.viewFullMessageButton, {marginBottom: 8}]}
                                                                            onPress={() => {
                                                                                setSelectedMessageForModal(messageFeedItem);
                                                                            }}
                                                                        >
                                                                        <ExpandSvg height={24} width={24} circleFill={isDarkMode ? 'rgba(255,255,255,0.1)' : '#f3f5fa'} arrowFill={isDarkMode ? '#FFFFFF' : '#023d83'} />
                                                                        </TouchableOpacity>
                                                                    )}
                                                                    <MessageHtmlFrame
                                                                        html={messageFeedItem.message_body_html}
                                                                        showImages={isEnabledFlag(messageFeedState.messageFeed.message_feed_show_html_images)}
                                                                        expandToFullHeight={isSingleHtmlMessage}
                                                                        isDarkMode={isDarkMode}
                                                                    />
                                                                </>
                                                            ) : (
                                                                <AppText
                                                                    style={[styles.messageText, typeStyles.messageText,
                                                                        isDarkMode && {color: isInbound ? theme.messageTextInbound : theme.messageTextOutbound},
                                                                    ]}>
                                                                    {messageFeedItem.message_body}
                                                                </AppText>
                                                            )}

                                                        </View>
                                                        {
                                                            messageFeedItem.attachments.length ?
                                                                <View style={[styles.messageAttachments]}>
                                                                    {
                                                                        messageFeedItem.attachments.map(attachment => {
                                                                            return (
                                                                                <TouchableOpacity
                                                                                    key={attachment.message_attachment_id}
                                                                                    style={styles.messageAttachmentWrapper}
                                                                                    onPress={() => {
                                                                                        window.open(attachment.message_attachment_external_path ? attachment.message_attachment_external_path : `${MESSAGE_ATTACHMENT_FILE_PATH}${attachment.message_attachment_file_name}`, '_blank');
                                                                                    }}
                                                                                >
                                                                                    <Image
                                                                                        style={[styles.messageAttachment]}
                                                                                        source={attachment.message_attachment_external_path ? attachment.message_attachment_external_path : `${MESSAGE_ATTACHMENT_FILE_PATH}${attachment.message_attachment_file_name}`}
                                                                                    />
                                                                                </TouchableOpacity>
                                                                            );
                                                                        })
                                                                    }
                                                                </View>
                                                                : null
                                                        }
                                                        <View
                                                            style={[basicStyles.flexRow, basicStyles.justifyContentSpaceBetween]}>
                                                            <AppText
                                                                style={[styles.messageInfoText, typeStyles.messageInfoText, {marginRight: 10},
                                                                    isDarkMode && {color: isOutbound ? theme.messageInfoTextOutbound : theme.text},
                                                                ]}>
                                                                {messageSender}
                                                            </AppText>
                                                            <View style={[basicStyles.flexRow, basicStyles.alignContentCenter]}>
                                                                <AppText
                                                                    style={[styles.messageInfoText, typeStyles.messageInfoText,
                                                                        isDarkMode && {color: isOutbound ? theme.messageInfoTextOutbound : theme.text},
                                                                    ]}>
                                                                    {isPendingMessage ? (
                                                                        messageFeedItem.is_delivery_confirmation_delayed
                                                                            ? Date.now() - new Date(messageFeedItem.message_created_timestamp).getTime() >= DELIVERY_CONFIRMATION_WAIT_MS
                                                                                ? 'Still waiting, will update automatically'
                                                                                : 'Waiting for delivery confirmation'
                                                                            : 'Sending...'
                                                                    ) : formatDate(messageFeedItem.message_created_timestamp, 'MM/DD/YYYY hh:mm a')}
                                                                </AppText>
                                                                {isOutbound && !isPendingMessage && messageFeedItem.message_id === recentlySentMessageId && (
                                                                    <View style={[styles.sentStatus, isDarkMode && {backgroundColor: theme.sentStatusBg || 'rgba(255,255,255,0.1)'}]}>
                                                                        <CheckMarkSvg width={12} height={12} fill={isDarkMode ? '#FFFFFF' : '#0065DB'} />
                                                                        <AppText style={[styles.sentStatusText, isDarkMode && {color: '#FFFFFF'}]}>
                                                                            Sent
                                                                        </AppText>
                                                                    </View>
                                                                )}
                                                            </View>
                                                        </View>
                                                    </View>
                                                    {
                                                        isOutbound ?
                                                            <Avatar
                                                                source={{uri: avatarUri}}
                                                                width={40}
                                                                style={{marginLeft: 20}}
                                                            />
                                                            : null
                                                    }
                                                </View>
                                            </View>
                                        </View>
                                    );
                                } else if (messageFeedItem.feedItemType === 'status') {
                                    return (
                                        <View
                                            key={`status-${messageFeedItem.message_feed_status_id}`}
                                            style={[
                                                styles.resolvedContentContainer,
                                                isMobile && styles.resolvedContentContainerMobile,
                                            ]}>
                                            {
                                                isEnabledFlag(messageFeedItem.message_feed_resolved) ?
                                                <MessageFeedResolvedSvg/>
                                                :
                                                <MessageFeedUnresolvedSvg/>
                                            }
                                            <AppText style={[
                                                styles.resolvedText,
                                                isMobile && styles.resolvedTextMobile,
                                                isMobile && styles.resolvedStatusTextMobile,
                                                isDarkMode && {color: theme.textSecondary},
                                            ]}>
                                                {getResolvedByText(messageFeedItem)}
                                            </AppText>
                                        </View>
                                    );
                                } else if(messageFeedItem.feedItemType === 'note') {
                                    return (
                                        <View
                                            key={`note-${messageFeedItem.message_feed_note_id}`}
                                            style={[styles.noteWrapperOuter, isDarkMode && {
                                                backgroundColor: theme.noteBg,
                                                borderColor: theme.noteBorder,
                                            }]}
                                        >
                                            <View>
                                                <AppText style={{color: '#000', textAlign: 'center'}}>
                                                    {getNoteByText(messageFeedItem)}
                                                </AppText>
                                                <AppText style={[
                                                    styles.noteContent,
                                                    isEnabledFlag(messageFeedItem.message_feed_note_is_block) ? styles.blockNote : {},
                                                    isEnabledFlag(messageFeedItem.message_feed_note_is_unblock) ? styles.unblockNote : {},
                                                ]}>
                                                    {messageFeedItem.message_feed_note_content}
                                                </AppText>
                                            </View>
                                        </View>
                                    );
                                }else if(messageFeedItem.feedItemType === 'assignment') {
                                    return (
                                        <View
                                            key={`status-${messageFeedItem.message_feed_assignment_id}`}
                                            style={[
                                                styles.resolvedContentContainer,
                                                isMobile && styles.resolvedContentContainerMobile,
                                            ]}>
                                            <AppText style={[
                                                styles.resolvedText,
                                                isMobile && styles.resolvedTextMobile,
                                                isDarkMode && {color: theme.textSecondary},
                                            ]}>
                                                {getAssignedByText(messageFeedItem)}
                                            </AppText>
                                        </View>
                                    );
                                }
                                return null;
                            })
                        }
                        </View>
                    </ScrollView>

                    <View style={[styles.messageFeedBottom, getThemeTransitionStyle(), isDarkMode && {
                        backgroundColor: theme.background,
                        borderTopWidth: 1,
                        borderTopColor: theme.border,
                    }]}>
                        <View style={[styles.usersTypingMessage]}>
                            {messageFeedState.usersTyping.length > 0 && (
                                <AppText style={isDarkMode ? {color: theme.text} : undefined}>{messageFeedState.usersTyping.join(', ')} {messageFeedState.usersTyping.length > 1 ? "are" : "is"} typing...</AppText>
                            )}
                        </View>
                        <View style={[styles.messageInputWrapper, getThemeTransitionStyle(), isDarkMode && {
                            backgroundColor: theme.inputBg,
                            borderColor: theme.inputBorder,
                        }]}
                        >
                            {messageFeedState.messageFeed.message_feed_email_address ? (
                                <View style={[styles.subjectSection, isDarkMode && {borderBottomColor: '#6B7385'}]}>
                                    {Platform.OS === 'web' ? (
                                        <input
                                            type="text"
                                            placeholder="Subject"
                                            value={newMessageSubject}
                                            onKeyDown={(event) => {
                                                if (event.key === ' ') {
                                                    event.stopPropagation();
                                                }
                                            }}
                                            onChange={(event) => {
                                                setNewMessageSubject(event.target.value);
                                            }}
                                            style={{
                                                ...styles.subjectInput,
                                                ...(isDarkMode ? {color: theme.text} : {}),
                                                border: 0,
                                                backgroundColor: 'transparent',
                                                outline: 'none',
                                                boxSizing: 'border-box',
                                                display: 'block',
                                                width: '100%',
                                                lineHeight: '22px',
                                                font: 'inherit',
                                            }}
                                        />
                                    ) : (
                                        <TextInput
                                            placeholder="Subject"
                                            placeholderTextColor={isDarkMode ? '#E1E1E1' : undefined}
                                            style={[styles.subjectInput, isDarkMode && {color: theme.text}]}
                                            onChangeText={value => {
                                                setNewMessageSubject(value);
                                            }}
                                            value={newMessageSubject}
                                        />
                                    )}
                                </View>
                            ) : null}
                            <View style={[styles.messageInputWrapperInner]}
                                  onClick={() => {
                                      messageInputRef.current.focus();
                                  }}
                            >
                                {Platform.OS === 'web' ? (
                                    <textarea
                                        ref={messageInputRef}
                                        placeholder="Message"
                                        value={newMessageBody}
                                        rows={1}
                                        cols={1}
                                        onKeyDown={(event) => {
                                            if (event.ctrlKey && event.key === 'Enter') {
                                                submitNewMessage();
                                            }
                                            if (event.key === ' ') {
                                                event.preventDefault();
                                                const target = event.target;
                                                const start = target.selectionStart;
                                                const end = target.selectionEnd;
                                                const val = newMessageBody;
                                                const newVal = val.substring(0, start) + ' ' + val.substring(end);
                                                target.value = newVal;
                                                target.selectionStart = start + 1;
                                                target.selectionEnd = start + 1;
                                                setNewMessageBody(newVal);
                                                updateWebMessageInputHeight(target);
                                            }
                                        }}
                                        onChange={(event) => {
                                            setNewMessageBody(event.target.value);
                                            updateWebMessageInputHeight(event.target);
                                        }}
                                        style={{
                                            ...styles.messageInput,
                                            ...(isDarkMode ? {color: theme.text} : {}),
                                            border: 0,
                                            backgroundColor: 'transparent',
                                            resize: 'none',
                                            overflow: 'hidden',
                                            outline: 'none',
                                            boxSizing: 'border-box',
                                            display: 'block',
                                            flex: '1 1 auto',
                                            width: '100%',
                                            minWidth: 0,
                                            lineHeight: '22px',
                                            height: `${webMessageInputHeight}px`,
                                            whiteSpace: 'pre-wrap',
                                            overflowWrap: 'break-word',
                                            wordBreak: 'break-word',
                                            font: 'inherit',
                                        }}
                                    />
                                ) : (
                                    <AutoExpandingTextInput
                                        onKeyPress={(event) => {
                                            if (event.ctrlKey && event.key === 'Enter') {
                                                submitNewMessage();
                                            }
                                        }}
                                        ref={messageInputRef}
                                        placeholder="Message"
                                        placeholderTextColor={isDarkMode ? '#E1E1E1' : undefined}
                                        style={[styles.messageInput, isDarkMode && {color: theme.text}]}
                                        onChangeText={value => {
                                            setNewMessageBody(value);
                                        }}
                                        value={newMessageBody}
                                    />
                                )}
                                <TouchableOpacity disabled={isSubmitting} onPress={() => {
                                    submitNewMessage();
                                }}>
                                    <BlueCircleArrowSvg width={40}/>
                                </TouchableOpacity>
                            </View>
                            <EmojiSelector
                                active={emojisActive}
                                onClose={() => {
                                    setEmojisActive(false);
                                }}
                                callback={(emoji) => {
                                    setNewMessageBody(current => {
                                        return current + emoji;
                                    });
                                }}
                            />
                            {
                                newMessageAttachments.length > 0 ?
                                    <View style={[styles.messageAttachments]}>
                                        {
                                            newMessageAttachments.map(messageAttachment => {
                                                return (
                                                    <TouchableOpacity
                                                        key={messageAttachment}
                                                        style={[styles.messageAttachmentWrapper]}
                                                        onPress={() => {
                                                            window.open(`${MESSAGE_ATTACHMENT_FILE_PATH}${messageAttachment}`, '_blank');
                                                        }}
                                                    >
                                                        <Image
                                                            style={[styles.messageAttachment]}
                                                            source={`${MESSAGE_ATTACHMENT_FILE_PATH}${messageAttachment}`}
                                                        />
                                                        <TouchableOpacity
                                                            style={[styles.removeAttachmentAction]}
                                                            onPress={(e) => {
                                                                e.stopPropagation();
                                                                removeAttachment(messageAttachment);
                                                            }}
                                                        >
                                                            <RemoveSvg width={15} height={15}/>
                                                        </TouchableOpacity>
                                                    </TouchableOpacity>
                                                );
                                            })
                                        }
                                    </View>
                                    : null
                            }
                        </View>
                        <View style={[styles.actionsContainer, {justifyContent: 'space-between'}]}>
                            <View style={{flexDirection: 'row', alignItems: 'center', flexWrap: isMobile ? 'nowrap' : 'wrap', flexShrink: 1}}>
                                <ActionIcon
                                    compact={isMobile}
                                    action={handleAttachmentImageUpload}
                                    svg={<AttachmentsSvg fill={isDarkMode ? '#FFFFFF' : '#3CA6F5'}/>}
                                    label="Attach Image or file"
                                    forUpload={true}
                                />
                                <ActionIcon
                                    compact={isMobile}
                                    action={() => {
                                        console.log('scripts pressed');
                                        setScriptModalActive(true);
                                    }}
                                    svg={<ScriptsSvg fill={isDarkMode ? '#FFFFFF' : '#3CA6F5'}/>}
                                    label="Scripts"
                                />
                                <ActionIcon
                                    compact={isMobile}
                                    action={() => {
                                        console.log('Notes pressed');
                                        setNoteModalOpen(true);
                                    }}
                                    svg={<NotesSvg fill={isDarkMode ? '#FFFFFF' : '#3CA6F5'}/>}
                                    label="Make a private note"
                                />
                                <ActionIcon
                                    compact={isMobile}
                                    action={() => {
                                        console.log('Emojis pressed');
                                        setEmojisActive(!emojisActive);
                                    }}
                                    svg={<EmojisSvg fill={isDarkMode ? '#FFFFFF' : '#3CA6F5'}/>}
                                    label="Emojis"
                                />
                                {(inboxAiAvailable || grammarCheck) && <ActionIcon
                                    compact={isMobile}
                                    action={fixGrammar}
                                    label={grammarChecking ? 'Checking grammar' : grammarReady ? 'Review grammar correction' : grammarRequestError ? 'Retry grammar check' : 'Fix grammar'}
                                    disabled={isSubmitting || grammarChecking || (!grammarReady && !newMessageBody.trim())}
                                    svg={<View style={{position: 'relative', alignItems: 'center', justifyContent: 'center', width: 20, height: 20}}>
                                        {grammarRequestError ? <AppText style={{color: isDarkMode ? '#ffb4ab' : '#b3261e', fontWeight: '700'}}>!</AppText> : grammarChecking ? <ActivityIndicator size="small" color="#0065db"/>
                                            : <AppText style={{color: grammarReady ? '#12B76A' : isDarkMode ? '#FFFFFF' : '#3CA6F5', fontWeight: '700'}}>AI</AppText>}
                                    </View>}
                                    badge={grammarReady ? <AppText pointerEvents="none" style={{position: 'absolute', top: -7, right: -7, zIndex: 2, width: 19, height: 19, borderRadius: 10, borderWidth: 2, borderColor: isDarkMode ? theme.background : '#FFFFFF', backgroundColor: '#12B76A', color: '#FFFFFF', fontSize: 12, lineHeight: 15, textAlign: 'center', fontWeight: '700'}}>✓</AppText> : null}
                                />}
                            </View>
                            <DarkModeToggle />
                        </View>
                        {grammarRequestError && <AppText accessibilityRole="alert" style={{fontSize: 12, color: isDarkMode ? '#ffb4ab' : '#b3261e', marginTop: 6}}>{grammarRequestError}</AppText>}
                    </View>
                </View>
                <View style={[
                    styles.messageFeedCustomerInfoContainer,
                    getThemeTransitionStyle(),
                    customerInfoActive ? styles.messageFeedCustomerInfoContainerActive : {},
                    isDarkMode && {backgroundColor: theme.background},
                ]}>
                    <MessageFeedCustomerInfo
                        organizationId={organizationId}
                        messageFeedId={messageFeedId}
                        setBlockModalActive={setBlockModalActive}
                        messageFeedBlocked={isEnabledFlag(messageFeedState.messageFeed.message_feed_blocked)}
                        updateSelectedMessageInList={updateSelectedMessageInList}
                        updateMessageFeed={updateMessageFeed}
                        updateFeedWithExistingData={updateFeedWithExistingData}
                        setCustomerInfoActive={setCustomerInfoActive}
                    />
                </View>
                {
                    blockModalActive ?
                        <BlockModal
                            messageFeed={messageFeedState.messageFeed}
                            handleSubmit={() => {
                                toggleMessageFeedBlocked();
                                setBlockModalActive(false);
                            }}
                            handleClose={() => {
                                setBlockModalActive(false);
                            }}
                            messageFeedBlocked={isEnabledFlag(messageFeedState.messageFeed.message_feed_blocked)}
                        />
                        : null
                }
                {
                    noteModalOpen ?
                        <CreateNewNoteModal
                            messageFeed={messageFeedState.messageFeed}
                            handleSubmit={(note) => {
                                submitMessageFeedNote(note);
                                setNoteModalOpen(false);
                            }}
                            handleClose={() => {
                                setNoteModalOpen(false);
                            }}
                        />
                        : null
                }
                {grammarPreviewOpen && grammarReady && <GrammarReviewModal
                    original={grammarCheck.originalText}
                    corrected={grammarCheck.result.text}
                    canApply={canApplyGrammar}
                    onClose={dismissGrammarReview}
                    onDismiss={dismissGrammarReview}
                    onApply={selectedText => {
                        if (!canApplyGrammar) return;
                        setNewMessageBody(selectedText || '');
                        clearGrammarCheck(messageFeedId);
                        setGrammarPreviewOpen(false);
                    }}
                />}
                <ScriptModal
                    active={scriptModalActive}
                    organizationId={organizationId}
                    onClose={() => {
                        setScriptModalActive(false);
                    }}
                    onSubmit={output => {
                        setNewMessageBody(output);
                    }}
                />
                {selectedMessageForModal && (
                    <MessageHtmlModal
                        messageSubject={selectedMessageForModal.message_subject}
                        messageBodyHtml={selectedMessageForModal.message_body_html}
                        messageCC={selectedMessageForModal.message_email_cc}
                        showImages={isEnabledFlag(messageFeedState.messageFeed.message_feed_show_html_images)}
                        handleClose={() => {
                            setSelectedMessageForModal(null);
                        }}
                    />
                )}
            </View>
        );
    }

    return (
        <View style={[basicStyles.flexCenterContent, basicStyles.flexScale, getThemeTransitionStyle(), isDarkMode && {backgroundColor: theme.background}]}>
            <ActivityIndicator size="large"/>
        </View>
    );
}

const mapStateToProps = (state) => {
    const {auth} = state;
    return {auth};
};

export default connect(mapStateToProps)(MessageFeed);

const styles = EStyleSheet.create({
    messageFeedWrapper: {
        width: '100%',
        flex: 1,
        flexDirection: 'row'
    },
    messageFeedContainer: {
        flex: 1,
        borderRightWidth: 1,
        borderRightColor: 'rgb(226, 233, 244)',
        backgroundColor: '#f8fafd',
    },
    messageFeedCustomerInfoContainer: {
        width: '33.333%',
        backgroundColor: '#f8fafd'
    },
    messageFeedStatusContainer: {
        flex: 'auto',
        flexGrow: 0,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: 8,
        paddingBottom: 8,
        paddingRight: 24,
        paddingLeft: 24,
        backgroundColor: '#F04438',
    },
    messageFeedStatusText: {
        color: '#FFF',
        fontFamily: 'SourceSansPro-light'
    },
    messageFeedResolved: {
        backgroundColor: '#12B76A'
    },
    messagesContainer: {
        padding: 24,
        flex: 1
    },
    messagesContainerExpanded: {
        flexGrow: 1,
        alignItems: 'stretch'
    },
    loadMoreContainer: {
        alignItems: 'center',
        paddingVertical: 10
    },
    loadMoreButton: {
        backgroundColor: '#F8FAFD',
        borderWidth: 1,
        borderColor: '#E2E9F4',
        borderRadius: 20,
        paddingHorizontal: 20,
        paddingVertical: 10,
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 150
    },
    loadMoreText: {
        color: '#0065DB',
        fontFamily: 'SourceSansPro-SemiBold',
        fontSize: 14
    },
    messageSlot: {
        marginBottom: 20
    },
    messageSlotExpanded: {
        flex: 1,
        marginBottom: 0,
        minHeight: 0,
        alignSelf: 'stretch'
    },
    messageWrapper: {
        maxWidth: '85%'
    },
    messageWrapperExpanded: {
        flex: 1,
        minHeight: 0,
        height: '100%'
    },
    messageInnerWrapper: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        flex: 1,
        minHeight: 0
    },
    messageInnerWrapperExpanded: {
        height: '100%',
        alignItems: 'stretch'
    },
    messageContentWrapper: {
        flex: 1
    },
    messageContentWrapperExpanded: {
        flex: 1,
        minHeight: 0,
        height: '100%'
    },
    messageBodyWrapperExpanded: {
        flex: 1,
        minHeight: 0,
        height: '100%'
    },
    messageSubjectHeaderContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    messageBodyWrapper: {
        borderRadius: 8,
        flex: 1,
        padding: 8,
        marginBottom: 5,
        minHeight: 0
    },
    messageText: {
        textWrap: 'wrap',
    },
    messageInfoText: {
        fontFamily: 'SourceSansPro-light'
    },
    sentStatus: {
        marginLeft: 10,
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 10,
        backgroundColor: '#E6F0FF',
        flexDirection: 'row',
        alignItems: 'center',
    },
    sentStatusText: {
        marginLeft: 4,
        color: '#0065DB',
        fontFamily: 'SourceSansPro-SemiBold',
        fontSize: 12,
    },
    subjectSeparator: {
        height: 1,
        width: '100%',
        marginBottom: 2,
    },
    resolvedContentContainer: {
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'row',
        marginTop: 10,
        marginBottom: 10,
    },
    resolvedText: {
        marginLeft: 5,
        textAlign: 'center',
        textWrap: 'balance',
        flexShrink: 1,
    },
    resolvedContentContainerMobile: {
        flexDirection: 'column',
        paddingHorizontal: 16,
    },
    resolvedTextMobile: {
        width: '100%',
        marginLeft: 0,
    },
    resolvedStatusTextMobile: {
        marginTop: 5,
    },
    resolvedIndicatorContainer: {
        scale: 0.6,
    },
    messageFeedBottom: {
        padding: 24
    },
    usersTypingMessage: {
        paddingLeft: 24,
        paddingRight: 76,
        position: 'absolute',
        bottom: '90%'
    },
    actionsContainer: {
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center'
    },
    subjectSection: {
        paddingTop: 5,
        paddingBottom: 5,
        paddingRight: 10,
        borderBottomWidth: 1,
        borderBottomColor: '#C0C9D6',
        width: '100%',
    },
    subjectInput: {
        flex: 1,
        minWidth: 0,
        marginLeft: 10,
        marginRight: 10,
    },
    messageInputWrapper: {
        backgroundColor: '#FFFFFF',
        paddingLeft: 20,
        paddingRight: 10,
        paddingTop: 5,
        paddingBottom: 5,
        borderWidth: 1,
        borderColor: '#E2E9F4',
        borderRadius: 30,
        width: '100%',
        marginBottom: 10,
    },
    messageInputWrapperInner: {
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        minWidth: 0
    },
    messageAttachments: {
        marginTop: 10,
        flexDirection: 'row',
        width: '100%',
    },
    messageAttachmentWrapper: {
        position: 'relative',
        marginRight: 10,
    },
    messageAttachment: {
        width: 44,
        height: 44,
        borderWidth: 1,
        borderColor: '#E2E9F4',
        borderRadius: 6,
    },
    messageInput: {
        flex: 1,
        minWidth: 0,
        marginLeft: 10,
        marginRight: 10,
    },
    removeAttachmentAction: {
        position: 'absolute',
        right: -7,
        top: -4
    },
    widthThird: {
        width: '33.333%'
    },
    noteWrapperOuter: {
        width: '100%',
        alignItems: 'center',
        marginTop: 35,
        marginBottom: 15,
        borderWidth: 1,
        borderColor: 'rgb(226, 233, 244)',
        backgroundColor: '#FFFF99',
        padding: 6
    },
    noteContent: {
        fontFamily: 'SourceSansPro-Light',
        color: '#000',
    },
    blockNote: {
        color: '#F04438'
    },
    unblockNote: {
        color: '#12B76A'
    },
    customerInfoTop: {
        padding: 15,
        flexDirection: 'row',
        alignItems: 'flex-start',
        borderBottomWidth: 1,
        borderBottomColor: '#E8EDF6',
        display: 'none'
    },
    contactNameTextInput: {
        fontSize: 16,
        width: '100%',
        marginBottom: 3,
        fontFamily: 'SourceSansPro-SemiBold',
    },
    contactPhoneNumber: {
        fontSize: 12,
        fontFamily: 'SourceSansPro-Light',
        color: '#9EABCC',
        textDecorationLine: "underline",
        textDecorationStyle: "solid",
        textDecorationColor: "#9EABCC"
    },
    mobileBackToListAction: {
        marginRight: 5,
        padding: 5,
        marginTop: 12,
        display: 'none'
    },
    viewFullMessageButton: {
        flexDirection: 'row',
        alignItems: 'center',
        marginLeft: 10,
        paddingVertical: 5,
        paddingLeft: 8,
        paddingRight: 0,
        borderRadius: 4,
    },
    viewFullMessageText: {
        fontSize: 12,
        fontFamily: 'SourceSansPro-SemiBold',
    },
    showImagesContainer: {
        alignItems: 'flex-end',
        paddingVertical: 10,
        paddingHorizontal: 24,
    },
    showImagesButton: {
        backgroundColor: '#F8FAFD',
        borderWidth: 1,
        borderColor: '#E2E9F4',
        borderRadius: 20,
        paddingHorizontal: 20,
        paddingVertical: 10,
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'row',
        minWidth: 150,
    },
    showImagesText: {
        color: '#0065DB',
        fontFamily: 'SourceSansPro-SemiBold',
        fontSize: 14,
        marginLeft: 8,
    },
    errorIndicatorContainer: {
        color: '#F04438',
        backgroundColor: '#FFF5F5',
        borderRadius: 100,
        width: 16,
        height: 16,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1,
        borderColor: '#F04438',
        marginRight: 4,
    },
    unreachableMessageBody: {
        backgroundColor: '#F04438',
    },
    '@media (max-width: 1000)': {
        messageFeedCustomerInfoContainer: {
            width: '100%',
            position: 'absolute',
            left: '100%',
            top: 34,
            bottom: 0,
        },
        messageFeedCustomerInfoContainerActive: {
            transform: [{translateX: '-100%'}]
        },
        customerInfoTop: {
            display: 'flex'
        }
    },
    '@media (max-width: 550)': {
        mobileBackToListAction: {
            display: 'flex'
        },
    }
});

const inboundStyles = EStyleSheet.create({
    messageSlot: {
        alignItems: 'flex-start',
    },
    messageBodyWrapper: {
        backgroundColor: '#DCDCDB'
    },
    messageText: {
        color: '#333333'
    },
    subjectSeparator: {
        backgroundColor: '#333333',
    },
    messageInfoText: {
        color: '#333333'
    }
});

const outboundStyles = EStyleSheet.create({
    messageSlot: {
        alignItems: 'flex-end',
    },
    messageBodyWrapper: {
        backgroundColor: '#0065DB'
    },
    messageText: {
        color: '#F8FAFD'
    },
    subjectSeparator: {
        backgroundColor: '#F8FAFD',
    },
    messageInfoText: {
        color: '#0065DB'
    }
});
